import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Badge,
  Divider,
  Spinner,
  useToast,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { CheckIcon, TimeIcon, DeleteIcon } from '@chakra-ui/icons';
import api from '../../../../../services/api';

/**
 * Modal to view and update tasks for a lead
 */
const NewKBTasksViewModal = ({ isOpen, onClose, lead }) => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const toast = useToast();
  
  // For delete confirmation dialog
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch tasks when modal opens
  useEffect(() => {
    if (isOpen && lead) {
      fetchTasks();
    }
  }, [isOpen, lead]);

  // Fetch tasks from API
  const fetchTasks = async () => {
    if (!lead || (!lead.leadId && !lead._id)) return;
    
    setIsLoading(true);
    try {
      const leadId = lead.leadId || lead._id;
      const response = await api.get(`/api/tasks/lead/${leadId}`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('[NewKBTasksViewModal] Error fetching tasks:', error);
      toast({
        title: 'Error fetching tasks',
        description: error.message || 'Could not load tasks',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update task status
  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    setIsUpdating(true);
    try {
      await api.put(`/api/tasks/${taskId}`, { status: newStatus });
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task._id === taskId ? { ...task, status: newStatus } : task
        )
      );
      
      toast({
        title: 'Task updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('[NewKBTasksViewModal] Error updating task:', error);
      toast({
        title: 'Error updating task',
        description: error.message || 'Could not update task',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Confirm delete dialog opener
  const confirmDelete = (taskId) => {
    setTaskToDelete(taskId);
    onAlertOpen();
  };
  
  // Delete task after confirmation
  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    setIsUpdating(true);
    try {
      await api.delete(`/api/tasks/${taskToDelete}`);
      
      // Update local state
      setTasks(prevTasks => prevTasks.filter(task => task._id !== taskToDelete));
      
      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('[NewKBTasksViewModal] Error deleting task:', error);
      toast({
        title: 'Error deleting task',
        description: error.message || 'Could not delete task',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      onAlertClose();
      setTaskToDelete(null);
    }
  };

  // Get color for priority badge
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  // Get color for status badge
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'pending': return 'yellow';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Tasks for {lead?.name || lead?.brideAndGroomName || 'Lead'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Flex justify="center" align="center" height="200px">
                <Spinner size="xl" />
              </Flex>
            ) : tasks.length === 0 ? (
              <Box textAlign="center" p={6}>
                <Text fontSize="lg" color="gray.500">
                  No tasks found for this lead
                </Text>
              </Box>
            ) : (
              <VStack spacing={4} align="stretch" maxH="500px" overflowY="auto">
                {tasks.map((task) => (
                  <Box
                    key={task._id}
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    position="relative"
                  >
                    <HStack justify="space-between" mb={2}>
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <Text fontWeight="bold">{task.title}</Text>
                          <Badge colorScheme={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                          <Badge colorScheme={getStatusColor(task.status)}>
                            {task.status}
                          </Badge>
                        </HStack>
                        {task.description && (
                          <Text fontSize="sm" color="gray.600">
                            {task.description}
                          </Text>
                        )}
                      </VStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => confirmDelete(task._id)}
                        isDisabled={isUpdating}
                      >
                        <DeleteIcon />
                      </Button>
                    </HStack>
                    
                    <Divider my={2} />
                    
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={0}>
                        <HStack fontSize="xs" color="gray.500">
                          <TimeIcon />
                          <Text>Due: {formatDate(task.dueDate)}</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          Assigned to: {task.assignedTo?.email || 'Unassigned'}
                        </Text>
                      </VStack>
                      
                      <Select
                        size="sm"
                        width="160px"
                        value={task.status}
                        onChange={(e) => handleUpdateTaskStatus(task._id, e.target.value)}
                        isDisabled={isUpdating}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </Select>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button 
              variant="outline" 
              colorScheme="blue" 
              onClick={fetchTasks}
              isLoading={isLoading}
              leftIcon={<CheckIcon />}
            >
              Refresh
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Task
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteTask} ml={3} isLoading={isUpdating}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default NewKBTasksViewModal;
